import { FormikProps, useFormikContext } from 'formik';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { SearchParamObject } from '@shared/interfaces/SearchParamObject';
import { EvalFiltersFields } from '../interfaces/EvalFiltersFields';
import { EvalFilterFields } from '../enums/EvalFilterFields';
import { filterSearchParamsObject } from '@shared/helpers/filterSearchParamsObject';
import { resetSearchResult } from '@modules/App/redux/users';
import { UserInfo } from '@shared/interfaces/UserInfo';
import { DepartmentData } from '@shared/interfaces/DepartmentData';
import { LocationOptionData } from '@shared/interfaces/LocationOptionData';
import { EmploymentType } from '@shared/enums/EmploymentType';
import { EvalStatuses } from '../enums/EvalStatuses';

export const useEvalHandlers = (allDepartmentsData: DepartmentData[], locations: LocationOptionData[]) => {
  const dispatch = useDispatch();
  const formik: FormikProps<EvalFiltersFields> = useFormikContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const searchParamsObject = (): SearchParamObject => {
    const obj: SearchParamObject = {};
    for (const [key, value] of searchParams.entries()) {
      obj[key] = value;
    }
    return obj;
  };

  const EXCLUSIVE_STATUSES = [EvalStatuses.All];

  const handleStatusChange = (fieldName: string, newValue: string[]) => {
    if (!newValue?.length) {
      setSearchParams({ ...searchParamsObject(), [fieldName]: EvalStatuses.All });
      formik.setFieldValue(fieldName, [EvalStatuses.All]);
      return;
    }

    const previousValue: string[] = formik.values.status || [];
    const newlyAdded = newValue.filter((s) => !previousValue.includes(s));
    const newlyAddedExclusive = newlyAdded.find((s) => EXCLUSIVE_STATUSES.includes(s as EvalStatuses));

    let value: string[];
    if (newlyAddedExclusive) {
      value = [newlyAddedExclusive];
    } else {
      value = newValue.filter((s) => !EXCLUSIVE_STATUSES.includes(s as EvalStatuses));
    }

    if (!value.length) {
      setSearchParams({ ...searchParamsObject(), [fieldName]: EvalStatuses.All });
      formik.setFieldValue(fieldName, [EvalStatuses.All]);
      return;
    }

    setSearchParams({ ...searchParamsObject(), [fieldName]: value.join(',') });
    formik.setFieldValue(fieldName, value);
  };

  const handleLocationChange = (fieldName: string, newValue: LocationOptionData[]) => {
    const selectedCountries: string[] = [];

    if (newValue.length < 1) {
      const obj = filterSearchParamsObject(searchParamsObject(), [fieldName]);
      setSearchParams(obj);
    } else {
      for (const selectedVal of newValue) {
        if (selectedVal.isLocationArea) {
          selectedCountries.push(
            ...locations
              .filter((area) => !area.isLocationArea && area.locationArea === selectedVal.locationArea)
              .map((area) => area.value)
          );
        } else {
          selectedCountries.push(selectedVal.value);
        }
      }
      setSearchParams({ ...searchParamsObject(), [fieldName]: selectedCountries.join(',') });
    }
    formik.setFieldValue(
      fieldName,
      newValue.map((v) => v.value)
    );
    dispatch(resetSearchResult());
  };

  const handleDepartmentsChange = (fieldName: string, newValue: string[]) => {
    if (newValue.length < 1) {
      const obj = filterSearchParamsObject(searchParamsObject(), [fieldName]);
      setSearchParams(obj);
    } else {
      const departmentIds = newValue.reduce((acc, departmentName) => {
        allDepartmentsData.map((dept) => {
          if (departmentName === dept.name) acc.push(dept.id);
        });
        return acc;
      }, [] as string[]);
      setSearchParams({ ...searchParamsObject(), [fieldName]: departmentIds.toString() });
    }
    formik.setFieldValue(fieldName, newValue);
  };

  const handlePeopleSelectorChange = (fieldName: string, newValue: UserInfo[]) => {
    const arrOfIds = newValue.reduce((acc, user) => {
      acc.push(user.id ?? '');
      return acc;
    }, [] as string[]);
    if (newValue.length < 1) {
      const obj = filterSearchParamsObject(searchParamsObject(), [fieldName]);
      setSearchParams(obj);
    } else {
      setSearchParams({ ...searchParamsObject(), [fieldName]: arrOfIds.toString() });
    }
    formik.setFieldValue(fieldName, newValue);
    dispatch(resetSearchResult());
  };

  const EOP_CLEANUP_FIELDS = [
    EvalFilterFields.EopStatus,
    EvalFilterFields.EopResolvedAtFrom,
    EvalFilterFields.EopResolvedAtTo,
    EvalFilterFields.EopExtendedToFrom,
    EvalFilterFields.EopExtendedToTo,
  ];

  const handleTypeToggle = (
    fieldName: string,
    newValue: boolean,
    oppositeField: EvalFilterFields,
    oppositeValue: boolean,
    cleanupFields?: EvalFilterFields[],
  ) => {
    const obj = searchParamsObject();

    if (newValue) {
      delete obj[fieldName];
    } else {
      obj[fieldName] = 'false';

      if (!oppositeValue) {
        formik.setFieldValue(oppositeField, true);
        delete obj[oppositeField];
      }

      if (cleanupFields) {
        for (const field of cleanupFields) {
          delete obj[field];
          formik.setFieldValue(field, null);
        }
      }
    }

    setSearchParams(obj);
    formik.setFieldValue(fieldName, newValue);
  };

  const handleIncludeRegularChange = (fieldName: string, newValue: boolean) => {
    handleTypeToggle(fieldName, newValue, EvalFilterFields.IncludeEop, formik.values.includeEop);
  };

  const handleIncludeEopChange = (fieldName: string, newValue: boolean) => {
    handleTypeToggle(fieldName, newValue, EvalFilterFields.IncludeRegular, formik.values.includeRegular, EOP_CLEANUP_FIELDS);
  };

  const handleEopStatusRadioChange = (fieldName: string, newValue: string) => {
    if (!newValue) {
      const obj = filterSearchParamsObject(searchParamsObject(), [fieldName]);
      setSearchParams(obj);
      formik.setFieldValue(fieldName, null);
    } else {
      setSearchParams({ ...searchParamsObject(), [fieldName]: newValue });
      formik.setFieldValue(fieldName, newValue);
    }
  };

  const handleRescheduledChange = (fieldName: string, newValue: boolean) => {
    if (newValue) {
      setSearchParams({ ...searchParamsObject(), [fieldName]: 'true' });
    } else {
      const obj = filterSearchParamsObject(searchParamsObject(), [fieldName]);
      setSearchParams(obj);
    }
    formik.setFieldValue(fieldName, newValue);
  };

  const handleDatesChange = (updates: Record<string, string | null>) => {
    const currentParams = searchParamsObject();
    const newParams = { ...currentParams };

    Object.entries(updates).forEach(([key, value]) => {
      if (value === null) {
        delete newParams[key];
      } else {
        newParams[key] = value;
      }
      formik.setFieldValue(key, value);
    });

    setSearchParams(newParams);
  };

  const handleEmploymentTypeChange = (fieldName: string, newValue: EmploymentType | null) => {
    if (!newValue) {
      const obj = filterSearchParamsObject(searchParamsObject(), [fieldName]);
      setSearchParams(obj);
    } else {
      setSearchParams({ ...searchParamsObject(), [fieldName]: newValue });
    }
    formik.setFieldValue(fieldName, newValue);
  };

  const handleHasOngoingFlagChange = (fieldName: string, newValue: boolean) => {
    if (newValue) {
      setSearchParams({ ...searchParamsObject(), [fieldName]: 'true' });
    } else {
      const obj = filterSearchParamsObject(searchParamsObject(), [fieldName]);
      setSearchParams(obj);
    }
    formik.setFieldValue(fieldName, newValue);
  };

  const handleEngagementModelsChange = (fieldName: string, newValue: string[]) => {
    if (!newValue || newValue.length === 0) {
      const obj = filterSearchParamsObject(searchParamsObject(), [fieldName]);
      setSearchParams(obj);
    } else {
      setSearchParams({ ...searchParamsObject(), [fieldName]: newValue.join(',') });
    }
    formik.setFieldValue(fieldName, newValue);
  };

  return {
    handleStatusChange,
    handleLocationChange,
    handleDepartmentsChange,
    handlePeopleSelectorChange,
    handleIncludeRegularChange,
    handleIncludeEopChange,
    handleEopStatusRadioChange,
    handleRescheduledChange,
    handleDatesChange,
    handleEmploymentTypeChange,
    handleHasOngoingFlagChange,
    handleEngagementModelsChange,
  };
};
